import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { configRestoreApi } from '../api/configRestoreApi';
import type { RestoreBaselineResult } from '../types/myProfile';
import type { ApiError } from '../types/myProfile';

/**
 * useRestoreBaseline — POST /api/eraf/admin/config/restore-baseline
 *
 * ROLE_ADMIN 전용 표준상태복구 mutation.
 * 성공 시 사이드바 메뉴 쿼리 무효화 + toast 표시.
 * 실패 시 error toast 표시.
 *
 * onSuccess/onError 콜백은 컴포넌트에서 addToast prop 으로 주입받음.
 */
export function useRestoreBaseline(
  addToast: (message: string, type: 'success' | 'error') => void
) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<RestoreBaselineResult, ApiError>({
    mutationFn: () => configRestoreApi.restoreBaseline(),
    onSuccess: (data) => {
      // 사이드바 메뉴 queryKey 무효화 — useNavMenu 에서 ['menu-management', 'me'] 사용
      void queryClient.invalidateQueries({ queryKey: ['menu-management', 'me'] });
      // auth/me 도 무효화하여 사용자 상태 최신화
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });

      addToast(
        t('myProfile.adminTools.restore.success', {
          menus: data.menusRestored,
          codes: data.codesRestored,
        }),
        'success'
      );
    },
    onError: (error) => {
      const msg =
        error?.message
          ? `${t('myProfile.adminTools.restore.error')} (${error.message})`
          : t('myProfile.adminTools.restore.error');
      addToast(msg, 'error');
    },
  });
}
